import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import MyTaskTable from "@/components/crud/table/user/MyTaskTable";
import useFetchTasks from "@/hooks/api/tasks/useFetchTask";
import useFetchTaskById from "@/hooks/api/tasks/useFetchTaskById";
import { taskKeys } from "@/hooks/api/tasks/keys";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { toast } from "sonner";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import {
  getTaskStatusOptions,
  taskStatusListSchema,
} from "@/lib/task-status-options";
import { z } from "zod";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { TaskOrdering } from "@/hooks/api/tasks/useFetchTask";
import { useDebounce } from "@/hooks/use-debounce";
import type { CreateTaskPayload, Task, TaskStatus } from "@/types/task";
import { TaskFormSheet } from "@/components/crud/forms/TaskFormSheet";
import { CompleteTaskDialog } from "@/components/crud/forms/CompleteTaskDialog";
import type { Subtask, SubtaskPayload } from "@/types/subtask";
import useStartTask from "@/hooks/api/tasks/useStartTask";
import useCompleteTask from "@/hooks/api/tasks/useCompleteTask";
import useStartSubtask from "@/hooks/api/tasks/subtask/useStartSubtask";
import useCompleteSubtask from "@/hooks/api/tasks/subtask/useCompleteSubtask";
import useCreateTask from "@/hooks/api/tasks/useCreateTask";
import useCreateSubtask from "@/hooks/api/tasks/subtask/useCreateSubtask";
import useUpdateTask from "@/hooks/api/tasks/useUpdateTask";
import { useCrmTaskMutations } from "@/hooks/api/crm/useCrmTasks";
import useUpdateSubtask from "@/hooks/api/tasks/subtask/useUpdateSubtask";
import useDeleteTask from "@/hooks/api/tasks/useDeleteTask";
import useDeleteSubtask from "@/hooks/api/tasks/subtask/useDeleteSubtask";
import { SubtaskFormSheet } from "@/components/crud/forms/SubtaskFormSheet";
import { UserTaskFormSheet } from "@/components/crud/forms/user/UserTaskFormSheet";
import { CrmTaskFormSheet } from "@/components/crm/forms/CrmTaskFormSheet";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import useSubmitTask from "@/hooks/api/tasks/approval/useSubmitTask";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";

// Filters live in the URL so they survive refresh and can be shared/bookmarked.
// Off-contract values degrade to undefined instead of failing the route.
const myTasksSearchSchema = z.object({
  status: taskStatusListSchema,
  source: z.enum(["crm", "all"]).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  taskId: z.number().optional().catch(undefined),
});

export const Route = createFileRoute("/user/_layout/my-tasks")({
  beforeLoad: () => requireFeature("tasks"),
  validateSearch: myTasksSearchSchema,
  component: MyTasks,
});

function MyTasks() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const startTask = useStartTask();
  const completeTask = useCompleteTask();
  const startSubtask = useStartSubtask();
  const completeSubtask = useCompleteSubtask();
  const createTask = useCreateTask();
  const createSubtask = useCreateSubtask();
  const updateTask = useUpdateTask();
  const updateSubtask = useUpdateSubtask();
  const deleteTask = useDeleteTask();
  const deleteSubtask = useDeleteSubtask();
  const submitTask = useSubmitTask();

  const { updateCrmTask } = useCrmTaskMutations();
  // Task/subtask mutation errors now surface via each hook's
  // meta.errorMessage (global toast) — no manual error-state effect needed.

  const taskApprovalRequired = useOrgSettingsStore(
    (s) => s.settings?.task_approval_required ?? true
  );

  const searchParams = Route.useSearch();
  const statusFilter = searchParams.status;
  const sourceFilter = searchParams.source ?? "hr";
  const setStatusFilter = useCallback(
    (values: TaskStatus[]) =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          status: values.length > 0 ? values : undefined,
        }),
        replace: true,
      }),
    [navigate]
  );
  const setSourceFilter = useCallback(
    (value: "hr" | "crm" | "all") =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          source: value === "hr" ? undefined : value,
        }),
        replace: true,
      }),
    [navigate]
  );
  const { ordering, toggleOrdering, getApiOrdering } = useDataTableOrdering<
    {
      title: SortDirection;
      status: SortDirection;
      priority: SortDirection;
      due_date: SortDirection;
      created_at: SortDirection;
    },
    TaskOrdering
  >({
    title: null,
    status: null,
    priority: null,
    due_date: null,
    created_at: null,
  });
  // Default to newest-created-first so a task keeps its place when its status
  // changes (e.g. after Start); an active column sort overrides this.
  const apiOrdering = getApiOrdering() ?? "-created_at";
  const [searchQuery, setSearchQuery] = useState(searchParams.q ?? "");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);

  // Persist the (debounced) search text in the URL alongside the dropdown filters.
  useEffect(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, navigate]);

  // UI state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCrmModalOpen, setIsCrmModalOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  const [editingCrmTask, setEditingCrmTask] = useState<Task | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Subtask creation/editing/viewing state
  const [isSubtaskSheetOpen, setIsSubtaskSheetOpen] = useState(false);
  const [parentTask, setParentTask] = useState<Task | null>(null);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);
  const [viewingSubtask, setViewingSubtask] = useState<Subtask | null>(null);
  const [editingTaskForForm, setEditingTaskForForm] = useState<Task | null>(
    null
  );
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [subtaskToDelete, setSubtaskToDelete] = useState<{
    subtask: Subtask;
    taskId: number;
  } | null>(null);
  const [isSubtaskDeleteDialogOpen, setIsSubtaskDeleteDialogOpen] =
    useState(false);

  const tasksQuery = useFetchTasks({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    status_in: statusFilter?.length ? statusFilter : undefined,
    source: sourceFilter === "all" ? undefined : sourceFilter,
    ordering: apiOrdering,
  });
  const tasks = tasksQuery.data?.results ?? [];
  const [taskToComplete, setTaskToComplete] = useState<{
    id: number;
    title?: string;
  } | null>(null);
  const count = tasksQuery.data?.count ?? 0;

  const isMutating =
    startTask.isPending ||
    completeTask.isPending ||
    startSubtask.isPending ||
    completeSubtask.isPending ||
    createTask.isPending ||
    createSubtask.isPending ||
    updateTask.isPending ||
    updateSubtask.isPending ||
    deleteTask.isPending ||
    deleteSubtask.isPending ||
    submitTask.isPending ||
    updateCrmTask.isPending;
  const loading = tasksQuery.isPending || isMutating;
  const isFetching = tasksQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  // Full filter/search clear — used only by the FiltersPanel "Clear" button.
  // Mutations that change the list's composition keep the URL filters and
  // search intact and just jump back to page 1 (see setPage below).
  const resetFilters = useCallback(() => {
    navigate({ to: ".", search: {}, replace: true });
    setSearchQuery("");
    resetDebounce();
    setPage(1);
  }, [navigate, resetDebounce, setPage]);

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sourceFilter, apiOrdering, setPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleView = (task: Task) => {
    setViewingTask(task);
    setIsSheetOpen(true);
  };

  // Deep-link from a notification: ?taskId=<id> opens the view-only sheet.
  // Clearing it drops only taskId so the URL-persisted filters survive.
  const deepLinkTaskId = searchParams.taskId ?? null;
  const clearDeepLink = () =>
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, taskId: undefined }),
      replace: true,
    });
  const deepLinkQuery = useFetchTaskById(deepLinkTaskId, false, {
    enabled: deepLinkTaskId != null,
  });
  useEffect(() => {
    if (deepLinkTaskId == null) return;
    if (deepLinkQuery.data) {
      handleView(deepLinkQuery.data);
    } else if (deepLinkQuery.isError) {
      clearDeepLink();
    }
    // handleView is stable enough for this one-shot open; deps track the id/data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTaskId, deepLinkQuery.data, deepLinkQuery.isError]);

  const handleCloseViewSheet = () => {
    setIsSheetOpen(false);
    setViewingTask(null);
    if (deepLinkTaskId != null) {
      clearDeepLink();
    }
  };

  const handleCreate = () => {
    setViewingTask(null);
    setEditingTaskForForm(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTaskForForm(task);
    setIsModalOpen(true);
  };

  const handleEditCrmTask = (task: Task) => {
    setEditingCrmTask(task);
    setIsCrmModalOpen(true);
  };

  const handleCrmTaskEditSubmit = async (id: number, data: Partial<import("@/types/crm").CrmTaskPayload>) => {
    await updateCrmTask.mutateAsync({ id, payload: data });
    toast.success("CRM task updated successfully!");
    setIsCrmModalOpen(false);
    setEditingCrmTask(null);
    // updateCrmTask self-invalidates the CRM domain's own query keys, but
    // this unified tasks list (source: "crm" rows included) is read from
    // the HR tasks endpoint/cache (taskKeys), which knows nothing about the
    // CRM cache — it needs its own explicit nudge to reflect the edit.
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
  };

  const handleAddSubtask = (task: Task) => {
    setParentTask(task);
    setEditingSubtask(null);
    setIsSubtaskSheetOpen(true);
  };

  const handleEditSubtask = (subtask: Subtask, taskId: number) => {
    const parent = tasks.find((t) => t.id === taskId);
    if (!parent) {
      toast.error("Parent task not found.");
      return;
    }
    setParentTask(parent);
    setEditingSubtask(subtask);
    setViewingSubtask(null);
    setIsSubtaskSheetOpen(true);
  };

  const handleViewSubtask = (subtask: Subtask, taskId: number) => {
    const parent = tasks.find((t) => t.id === taskId);
    setParentTask(parent ?? null);
    setEditingSubtask(subtask);
    setViewingSubtask(subtask);
    setIsSubtaskSheetOpen(true);
  };

  const handleDeleteTaskClick = (task: Task) => {
    setTaskToDelete(task);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteSubtaskClick = (subtask: Subtask, taskId: number) => {
    setSubtaskToDelete({ subtask, taskId });
    setIsSubtaskDeleteDialogOpen(true);
  };

  const handleDeleteTaskConfirm = async () => {
    if (!taskToDelete) return;
    await deleteTask.mutateAsync({ id: taskToDelete.id });
    toast.success("Task deleted successfully!");
    setIsDeleteDialogOpen(false);
    setTaskToDelete(null);
    setPage(1);
  };

  const handleDeleteSubtaskConfirm = async () => {
    if (!subtaskToDelete) return;
    await deleteSubtask.mutateAsync({
      taskId: subtaskToDelete.taskId,
      id: subtaskToDelete.subtask.id,
    });
    toast.success("Subtask deleted successfully!");
    setIsSubtaskDeleteDialogOpen(false);
    setSubtaskToDelete(null);
    setPage(1);
  };

  // Parent task handlers
  const handleStartTask = async (id: number) => {
    await startTask.mutateAsync({ id });
    if (viewingTask?.id === id) {
      setViewingTask((prev) =>
        prev ? { ...prev, status: "in_progress" } : prev
      );
    }
  };

  const handleSubmitTask = async (id: number) => {
    try {
      await submitTask.mutateAsync({ id });
      toast.success("Task submitted for approval!");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "";
      if (msg.toLowerCase().includes("not required")) {
        submitTask.reset();
        await startTask.mutateAsync({ id });
        toast.success("Task started!");
      }
    }
  };

  // Completion opens a dialog so the user can record what was done.
  // async so it satisfies the sheet's `(id) => Promise<void>` prop type.
  const handleCompleteTask = async (id: number) => {
    setTaskToComplete({ id, title: tasks.find((t) => t.id === id)?.title });
  };

  const handleCompleteConfirm = async (remarks: string) => {
    if (!taskToComplete) return;
    const { id } = taskToComplete;
    await completeTask.mutateAsync({
      id,
      payload: { completion_remarks: remarks },
    });
    if (viewingTask?.id === id) {
      setViewingTask((prev) =>
        prev ? { ...prev, status: "completed" } : prev
      );
    }
    setTaskToComplete(null);
  };

  // Subtask own status handlers
  const handleStartSubtaskOwn = async (subtask: Subtask, taskId: number) => {
    await startSubtask.mutateAsync({ taskId, id: subtask.id });
  };

  const handleCompleteSubtaskOwn = async (subtask: Subtask, taskId: number) => {
    await completeSubtask.mutateAsync({ taskId, id: subtask.id });
  };

  const handleStartSubtaskView = async (id: number) => {
    if (!viewingTask) return;
    await startSubtask.mutateAsync({ taskId: viewingTask.id, id });
  };

  const handleCompleteSubtaskView = async (id: number) => {
    if (!viewingTask) return;
    await completeSubtask.mutateAsync({ taskId: viewingTask.id, id });
  };

  // Subtask list inside parent task view
  const handleUpdateSubtaskStatus = async (
    taskId: number,
    subtask: Subtask,
    status: "todo" | "in_progress" | "completed"
  ) => {
    if (status === "in_progress") {
      await startSubtask.mutateAsync({ taskId, id: subtask.id });
    } else if (status === "completed") {
      await completeSubtask.mutateAsync({ taskId, id: subtask.id });
    }
    setViewingTask((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subtasks: prev.subtasks?.map((s) =>
          s.id === subtask.id ? { ...s, status } : s
        ),
      };
    });
  };

  const handleFormSubmit = async (data: CreateTaskPayload) => {
    if (editingTaskForForm) {
      await updateTask.mutateAsync({ id: editingTaskForForm.id, payload: data });
      toast.success("Task updated successfully!");
    } else {
      await createTask.mutateAsync({ payload: data });
      toast.success("Task created successfully!");
    }
    setIsModalOpen(false);
    setEditingTaskForForm(null);
    setPage(1);
  };

  const handleSubtaskFormSubmit = async (data: SubtaskPayload) => {
    if (!parentTask) return;
    if (editingSubtask) {
      await updateSubtask.mutateAsync({
        taskId: parentTask.id,
        id: editingSubtask.id,
        payload: data,
      });
      toast.success("Subtask updated successfully!");
    } else {
      await createSubtask.mutateAsync({ taskId: parentTask.id, payload: data });
      toast.success("Subtask created successfully!");
    }
    setIsSubtaskSheetOpen(false);
    setEditingSubtask(null);
    setPage(1);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="My Tasks"
          description="View and track all your assigned tasks."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleCreate}
          addLabel="Add Task"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Source"
            options={[
              { label: "Normal", value: "hr" },
              { label: "All", value: "all" },
              { label: "CRM", value: "crm" },
            ]}
            value={sourceFilter}
            onChange={setSourceFilter}
          />

          <MultiSelectFilter
            placeholder="Status"
            options={getTaskStatusOptions({ taskApprovalRequired })}
            values={statusFilter ?? []}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <MyTaskTable
          tasks={tasks}
          loading={loading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onTitleClick={(id) =>
            navigate({ to: "/user/my-tasks/$id", params: { id: String(id) } })
          }
          onView={handleView}
          onViewSubtask={handleViewSubtask}
          onStartTask={(task) => handleStartTask(task.id)}
          onCompleteTask={(task) => handleCompleteTask(task.id)}
          onSubmitTask={(task) => handleSubmitTask(task.id)}
          onStartSubtask={handleStartSubtaskOwn}
          onCompleteSubtask={handleCompleteSubtaskOwn}
          onAddSubtask={handleAddSubtask}
          onEdit={handleEditTask}
          onEditCrmTask={handleEditCrmTask}
          onEditSubtask={handleEditSubtask}
          onDelete={handleDeleteTaskClick}
          onDeleteSubtask={handleDeleteSubtaskClick}
          showSourceColumn={sourceFilter === "all" || sourceFilter === "crm"}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <TaskFormSheet
          isOpen={isSheetOpen}
          isViewOnly={true}
          onClose={handleCloseViewSheet}
          onSubmit={async () => {}}
          users={[]}
          onSearchUsers={async () => {}}
          editingTask={viewingTask}
          isLoading={loading}
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onStartSubtask={handleStartSubtaskView}
          onCompleteSubtask={handleCompleteSubtaskView}
          onUpdateSubtaskStatus={handleUpdateSubtaskStatus}
          actionLoading={
            startTask.isPending ||
            completeTask.isPending ||
            startSubtask.isPending ||
            completeSubtask.isPending ||
            submitTask.isPending
          }
        />

        <UserTaskFormSheet
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTaskForForm(null);
          }}
          onSubmit={handleFormSubmit}
          isLoading={createTask.isPending || updateTask.isPending}
          editingTask={editingTaskForForm}
        />

        <CrmTaskFormSheet
          isOpen={isCrmModalOpen}
          onClose={() => {
            setIsCrmModalOpen(false);
            setEditingCrmTask(null);
          }}
          onCreate={async () => {}}
          onEdit={handleCrmTaskEditSubmit}
          editingTask={editingCrmTask}
          isLoading={updateCrmTask.isPending}
        />

        <SubtaskFormSheet
          isOpen={isSubtaskSheetOpen}
          onClose={() => {
            setIsSubtaskSheetOpen(false);
            setEditingSubtask(null);
            setViewingSubtask(null);
          }}
          onSubmit={handleSubtaskFormSubmit}
          parentTaskTitle={parentTask?.title}
          isLoading={createSubtask.isPending || updateSubtask.isPending}
          editingSubtask={editingSubtask}
          isViewOnly={viewingSubtask != null}
          onEditClick={() => setViewingSubtask(null)}
        />

        <CompleteTaskDialog
          isOpen={!!taskToComplete}
          onClose={() => setTaskToComplete(null)}
          taskTitle={taskToComplete?.title}
          isLoading={completeTask.isPending}
          onConfirm={handleCompleteConfirm}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={handleDeleteTaskConfirm}
          itemName={taskToDelete?.title}
          isLoading={deleteTask.isPending}
        />

        <DeleteConfirmDialog
          isOpen={isSubtaskDeleteDialogOpen}
          onClose={() => {
            setIsSubtaskDeleteDialogOpen(false);
            setSubtaskToDelete(null);
          }}
          onConfirm={handleDeleteSubtaskConfirm}
          itemName={subtaskToDelete?.subtask.title}
          isLoading={deleteSubtask.isPending}
        />
      </div>
    </SidebarInset>
  );
}
